import { useCallback, useEffect, useMemo, useState } from "react";
import { createViewUploadFinJournals } from "@/modules/lasalle/ingestion/views/ViewUploadFinJournals";
import { createViewUploadStandaloneFinJournals } from "@/modules/lasalle/ingestion/views/ViewUploadStandaloneFinJournals";
import { createViewUploadTBGL } from "@/modules/lasalle/ingestion/views/ViewUploadTBGL";
import { createViewUploadSupporting } from "@/modules/lasalle/ingestion/views/ViewUploadSupporting";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

export const wireUpload = ({ store: _store, useAppStore, api }: TCompositionDeps) => {
    const ViewUploadFinJournals = createViewUploadFinJournals({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const currentUser = useAppStore((state) => state.user);

            const canUploadFinJournals = useMemo(() => {
                return currentUser?.permissions.includes("finjournal_file_upload") ?? false;
            }, [currentUser]);

            const [deals, setDeals] = useState<{
                data: Array<{ id: string; name: string; code: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const loadDeals = useCallback(() => {
                setDeals({ data: null, loading: true });
                api.deals
                    .list({ active_only: true, limit: 100 })
                    .then((response) => {
                        setDeals({
                            data: response.items.map((d) => ({
                                id: d.id,
                                name: d.name,
                                code: d.code || "",
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setDeals({ data: [], loading: false });
                    });
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            return {
                onMount: loadDeals,
                onUnmount: NOOP,
                useDeals: () => deals,
                onUploadFile: useCallback(
                    async (data: {
                        dealId: string;
                        period: string;
                        fileType: string;
                        file: File;
                        sheetName?: string;
                        contributorIds?: string[];
                    }) => {
                        await api.files.upload(data.file, {
                            deal_id: data.dealId,
                            period: data.period,
                            file_type: data.fileType as
                                | "trial_balance"
                                | "general_ledger"
                                | "open_trial_balance"
                                | "financial_journals",
                            sheet_name: data.sheetName,
                            contributor_ids: data.contributorIds,
                        });
                    },
                    [],
                ),
                useContributorCandidates: (dealId: string | null) => {
                    const [candidates, setCandidates] = useState<{
                        data: { id: string; label: string }[];
                        loading: boolean;
                    }>({ data: [], loading: false });
                    useEffect(() => {
                        if (!dealId) {
                            setCandidates({ data: [], loading: false });
                            return;
                        }
                        let cancelled = false;
                        setCandidates((s) => ({ ...s, loading: true }));
                        api.finjournals
                            .getContributorCandidates(dealId)
                            .then((rows) => {
                                if (cancelled) return;
                                setCandidates({
                                    data: rows.map((r) => ({ id: r.id, label: r.display_name })),
                                    loading: false,
                                });
                            })
                            .catch(() => {
                                if (!cancelled) setCandidates({ data: [], loading: false });
                            });
                        return () => {
                            cancelled = true;
                        };
                    }, [dealId]);
                    return candidates;
                },
                canUpload: canUploadFinJournals,
            };
        },
    });

    const ViewUploadStandaloneFinJournals = createViewUploadStandaloneFinJournals({
        useCases: () => {
            const currentUser = useAppStore((state) => state.user);

            const canUpload = useMemo(() => {
                return currentUser?.permissions.includes("finjournal_file_upload") ?? false;
            }, [currentUser]);

            return {
                onUploadFile: useCallback(async (file: File, contributorIds: string[]) => {
                    const result = await api.finjournals.uploadStandalone(file, {
                        contributorIds,
                    });
                    return { message: result.message };
                }, []),
                useContributorCandidates: () => {
                    const [candidates, setCandidates] = useState<{
                        data: { id: string; label: string }[];
                        loading: boolean;
                    }>({ data: [], loading: true });
                    useEffect(() => {
                        let cancelled = false;
                        setCandidates((s) => ({ ...s, loading: true }));
                        api.finjournals
                            .getContributorCandidates(null)
                            .then((rows) => {
                                if (cancelled) return;
                                setCandidates({
                                    data: rows.map((r) => ({ id: r.id, label: r.display_name })),
                                    loading: false,
                                });
                            })
                            .catch(() => {
                                if (!cancelled) setCandidates({ data: [], loading: false });
                            });
                        return () => {
                            cancelled = true;
                        };
                    }, []);
                    return candidates;
                },
                canUpload,
            };
        },
    });

    const ViewUploadTBGL = createViewUploadTBGL({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const currentUser = useAppStore((state) => state.user);

            const canUploadTBGL = useMemo(() => {
                return currentUser?.permissions.includes("tbgl_file_upload") ?? false;
            }, [currentUser]);
            const canViewTBGLFiles = useMemo(() => {
                return currentUser?.permissions.includes("tbgl_files_page_access") ?? false;
            }, [currentUser]);
            const [deals, setDeals] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                    isLive?: boolean;
                    acceptsOtb?: boolean;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const loadDeals = useCallback(() => {
                setDeals({ data: null, loading: true });
                api.deals
                    .list({ sp_active_only: true, active_only: true, limit: 100 })
                    .then((response) => {
                        setDeals({
                            data: response.items.map((d) => ({
                                id: d.id,
                                name: d.business_partner_name
                                    ? `${d.business_partner_name} : ${d.name}`
                                    : d.name,
                                code: d.code || "",
                                // TB/GL uploads are held back until the deal's OTB is committed.
                                isLive: d.is_live,
                                acceptsOtb: d.accepts_otb,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setDeals({ data: [], loading: false });
                    });
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            return {
                onMount: loadDeals,
                onUnmount: NOOP,
                useDeals: () => deals,
                onUploadFile: useCallback(
                    async (data: {
                        dealId: string;
                        period: string;
                        fileType: string;
                        file: File;
                        sheetName?: string;
                        tbSheetName?: string;
                        glSheetName?: string;
                    }) => {
                        await api.files.upload(data.file, {
                            deal_id: data.dealId,
                            period: data.period,
                            file_type: data.fileType as
                                | "trial_balance"
                                | "general_ledger"
                                | "open_trial_balance"
                                | "financial_journals"
                                | "tb_gl",
                            sheet_name: data.sheetName,
                            tb_sheet_name: data.tbSheetName,
                            gl_sheet_name: data.glSheetName,
                        });
                    },
                    [],
                ),
                canUpload: canUploadTBGL,
                canViewFiles: canViewTBGLFiles,
            };
        },
    });

    const ViewUploadSupporting = createViewUploadSupporting({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const currentUser = useAppStore((state) => state.user);

            const canUploadSupporting = useMemo(() => {
                return currentUser?.permissions.includes("supporting_file_upload") ?? false;
            }, [currentUser]);
            const [deals, setDeals] = useState<{
                data: Array<{ id: string; name: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            // Only deals whose business partner allows supporting files — the
            // upload endpoint rejects the rest anyway.
            const loadDeals = useCallback(() => {
                setDeals({ data: null, loading: true });
                api.deals
                    .list({ can_upload_sf_only: true, active_only: true, limit: 100 })
                    .then((response) => {
                        setDeals({
                            data: response.items.map((d) => ({
                                id: d.id,
                                name: d.business_partner_name
                                    ? `${d.business_partner_name} : ${d.name}`
                                    : d.name,
                            })),
                            loading: false,
                        });
                    })
                    .catch(() => {
                        setDeals({ data: [], loading: false });
                    });
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            return {
                onMount: loadDeals,
                onUnmount: NOOP,
                useDeals: () => deals,
                useFinancialFiles: (dealId: string | null) => {
                    const [financialFiles, setFinancialFiles] = useState<{
                        data: Array<{ id: string; name: string }> | null;
                        loading: boolean;
                    }>({ data: null, loading: false });
                    useEffect(() => {
                        if (!dealId) {
                            setFinancialFiles({ data: null, loading: false });
                            return;
                        }
                        let cancelled = false;
                        setFinancialFiles({ data: null, loading: true });
                        api.supportingFiles
                            .relatableFiles(dealId)
                            .then((files) => {
                                if (cancelled) return;
                                setFinancialFiles({
                                    data: files.map((f) => ({ id: f.id, name: f.name })),
                                    loading: false,
                                });
                            })
                            .catch(() => {
                                if (!cancelled) setFinancialFiles({ data: [], loading: false });
                            });
                        return () => {
                            cancelled = true;
                        };
                    }, [dealId]);
                    return financialFiles;
                },
                canUpload: canUploadSupporting,
                onUpload: useCallback(
                    async (data: {
                        dealId: string;
                        friendlyName: string;
                        period: string;
                        financialFileId: string | null;
                        notes: string | null;
                        file: File;
                    }) => {
                        await api.supportingFiles.upload(data.file, {
                            deal_id: data.dealId,
                            friendly_name: data.friendlyName,
                            // YYYY-MM from the picker; the endpoint appends "-01".
                            period: data.period,
                            financial_file_id: data.financialFileId || undefined,
                            notes: data.notes || undefined,
                        });
                    },
                    [],
                ),
            };
        },
    });

    return {
        ViewUploadFinJournals,
        ViewUploadStandaloneFinJournals,
        ViewUploadTBGL,
        ViewUploadSupporting,
    };
};
